import { ClubPost, ClubTag, DEFAULT_RELATED_POST_CURSOR_PAGINATION, isPromo, PostItemService } from '@alliance/clubs/domain'
import { HeaderMainService } from '@alliance/clubs/header'
import { SidebarMainService } from '@alliance/clubs/sidebar'
import { BreadcrumbsItem, ImageAttributesHelperService, RoutingService, SidebarTypesEnum, TWITTER_BUDNI } from '@alliance/clubs/utils'
import { HeaderPositionModeEnum } from '@alliance/shared/header/utils'
import { SkeletonClubsPostDesktop, SkeletonClubsPostMobile } from '@alliance/shared/icons'
import { ImageAttributesType, RxStateComponent } from '@alliance/shared/models'
import { DetectPlatformService, DynamicFilesLoaderService, MediaService, OpenGraphHelperService } from '@alliance/shared/utils'
import { ChangeDetectionStrategy, Component, Inject, OnDestroy } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { WINDOW } from '@ng-web-apis/common'
import { Observable } from 'rxjs'
import { filter, map, switchMap, tap } from 'rxjs/operators'
import { Translations } from '../localization/translations'
import { SeoService } from '@alliance/shared/seo'
import { BudniSeoKeysEnum } from '@alliance/clubs/seo'
import { getDateFromString, format } from '@alliance/shared/datetime'

@Component({
  selector: 'alliance-clubs-post-page',
  templateUrl: './post-page.component.html',
  styleUrls: ['./post-page.component.tw.css', './post-page-responsive.component.tw.css'],
  providers: [ImageAttributesHelperService],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PostPageComponent
  extends RxStateComponent<{
    slug: string
    post: ClubPost | null
    isMobileScreen: boolean
    breadcrumbs: BreadcrumbsItem[] | null
    isPostWithRiddleTest: boolean
    imageAttributes: ImageAttributesType
  }>
  implements OnDestroy
{
  public state = this.connectViewState(['post', 'isMobileScreen', 'breadcrumbs', 'imageAttributes'])
  public readonly SkeletonClubsPostDesktop = SkeletonClubsPostDesktop
  public readonly SkeletonClubsPostMobile = SkeletonClubsPostMobile

  public constructor(
    private postService: PostItemService,
    private route: ActivatedRoute,
    private router: Router,
    private mediaService: MediaService,
    private headerMainService: HeaderMainService,
    private sidebarMainService: SidebarMainService,
    private routingService: RoutingService,
    public translations: Translations,
    @Inject(WINDOW) private readonly windowRef: Window,
    private openGraphHelperService: OpenGraphHelperService,
    private filesLoaderService: DynamicFilesLoaderService,
    private imageAttributesHelperService: ImageAttributesHelperService,
    private platform: DetectPlatformService,
    private seoService: SeoService
  ) {
    super()

    this.initState({
      slug: this.getSlug$(),
      post: this.select('slug').pipe(
        tap(() => this.set({ post: null })),
        switchMap(slug => this.postService.getPost$(slug, DEFAULT_RELATED_POST_CURSOR_PAGINATION).pipe(filter(data => !!data)))
      ),
      isMobileScreen: this.mediaService.select('isMobileScreen'),
      breadcrumbs: this.getBreadcrumbs$(),
      isPostWithRiddleTest: false,
      imageAttributes: this.select('post').pipe(
        filter(Boolean),
        map(data => this.imageAttributesHelperService.getPostImageAttributes(data.title))
      )
    })

    if (this.platform.isServer) {
      this.setSeoParams()
    }

    this.hold(this.select('isPostWithRiddleTest').pipe(filter(Boolean)), () => {
      this.filesLoaderService.loadFile('https://www.riddle.com/embed/files/js/embed.js', 'riddleJS', 'script', '1.0')
    })
  }

  public openLinksOnNewTab(e: Event): void {
    const link = e.composedPath().find(event => event instanceof Element && event.tagName.toLowerCase() === 'a')
    const href = link && link instanceof HTMLAnchorElement && link.href
    if (href) {
      e.preventDefault()
      this.windowRef.open(href, '_blank')
    }
  }

  public isPromo(tags: ClubTag[]): boolean {
    return isPromo(tags)
  }

  // FOR CLEANING ALL GLOBAL VARIABLES & LISTENERS FROM RIDDLE JS
  public override ngOnDestroy(): void {
    super.ngOnDestroy()

    const { isPostWithRiddleTest } = this.get()
    if (isPostWithRiddleTest) {
      this.windowRef.location.reload()
    }
  }

  private hasRiddleTest(data: string): boolean {
    const isRiddle = /riddle_target/gm
    return !!data.match(isRiddle)
  }

  private getSlug$(): Observable<string> {
    return this.route.paramMap.pipe(
      map(params => params.get('postSegmentsSlug')),
      filter(Boolean),
      tap(() => {
        this.headerMainService.set({ mode: HeaderPositionModeEnum.sticky, showBackButton: true })
        this.sidebarMainService.set({ type: SidebarTypesEnum.outer })
      })
    )
  }

  private getBreadcrumbs$(): Observable<BreadcrumbsItem[] | null> {
    return this.select('post').pipe(
      filter(Boolean),
      map(post => [
        {
          label: post.topics[0].name,
          path: this.routingService.getRubricPageRouterLink({
            rubricName: post.topics[0].engname
          })
        },
        {
          label: post.title,
          path: this.router.url
        }
      ])
    )
  }

  private setSeoParams(): void {
    this.hold(
      this.select('post').pipe(
        filter(post => !!post),
        switchMap(post => {
          this.set({ isPostWithRiddleTest: this.hasRiddleTest(post?.body ?? '') })
          const publishedDate = getDateFromString(post?.publishedAt || '')
          return this.seoService.setSeoParams$({
            key: BudniSeoKeysEnum.budniPostPage,
            params: {
              id: this.windowRef.location.href,
              headline: (post?.title ?? '').trim(),
              description: post?.description ?? '',
              postSlug: post?.slug ?? '',
              image: post?.thumbnail ?? '',
              authorName: post?.userProfile?.fullName ?? '',
              datePublished: publishedDate ? format(publishedDate, 'yyyy-MM-dd') : '',
              rubricName: post?.topics[0].name ?? '',
              rubricEngname: post?.topics[0].engname ?? ''
            }
          })
        })
      ),
      seoData => {
        const { post } = this.get()
        this.openGraphHelperService.addOgTags({
          url: seoData.canonicalUrl,
          title: seoData.title,
          description: seoData.description,
          image: post?.thumbnail,
          twitterSite: TWITTER_BUDNI,
          twitterTitle: seoData.title,
          twitterDescription: seoData.description
        })
      }
    )
  }
}
